"use client"

import { useState, useEffect, useRef } from "react"
import type { BooruPost } from "@/lib/booru-api"

export interface TopScript {
  id: string
  priceCents: number
  purchaseCount: number
}

// Returns per-post script counts, and the single most-popular script per post
// (for the grid quick-play button — see docs/SCRIPTS_ONLY_GRID_TASK_PLAN.md,
// Lane D) — both keyed by "site:id", from one batched call.
export function usePostScriptCounts(posts: BooruPost[]) {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [topScripts, setTopScripts] = useState<Record<string, TopScript>>({})
  const prevKeyRef = useRef("")

  useEffect(() => {
    if (posts.length === 0) return

    // Build a stable key to avoid re-fetching when posts reference changes but IDs are the same
    const key = posts.map((p) => `${p.site}:${p.id}`).join(",")
    if (key === prevKeyRef.current) return
    prevKeyRef.current = key

    const payload = posts.map((p) => ({ id: String(p.id), site: p.site }))

    // Debounce — wait for posts to stabilize before firing
    const timer = setTimeout(() => {
      fetch("/api/marketplace/scripts/counts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ posts: payload }),
      })
        .then((r) => r.ok ? r.json() : { counts: {}, topScripts: {} })
        .then((data) => {
          setCounts((prev) => ({ ...prev, ...data.counts }))
          setTopScripts((prev) => ({ ...prev, ...data.topScripts }))
        })
        .catch(() => {})
    }, 300)

    return () => clearTimeout(timer)
  }, [posts])

  const getCount = (post: BooruPost) => counts[`${post.site}:${post.id}`] ?? 0
  const getTopScript = (post: BooruPost): TopScript | undefined => topScripts[`${post.site}:${post.id}`]

  return { counts, getCount, topScripts, getTopScript }
}
